// src/Redux/UtilsRedux/axiosWithAuth.js
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { clearAuthState } from '../Auth/authSlice'; // actions are safe to import (no cycle)

// ❗️Do NOT import the store here.
// We'll inject dispatch after the store is created:
let dispatchRef = null;
export const attachStore = (store) => { dispatchRef = store.dispatch; };

const axiosWithAuth = axios.create({
  baseURL: process.env.REACT_APP_API_BASE,
});

// --- helper: wait until Firebase exposes currentUser (with timeout) ---
function waitForFirebaseUser(timeoutMs = 3000) {
  const auth = getAuth();
  if (auth.currentUser) return Promise.resolve(auth.currentUser);

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      resolve(getAuth().currentUser || null);
    }, timeoutMs);

    const unsubscribe = auth.onIdTokenChanged((u) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(u || null);
    });
  });
}

// ---------- REQUEST ----------
axiosWithAuth.interceptors.request.use(async (config) => {
  // Wait briefly for Firebase to hydrate on hard refresh
  const user = getAuth().currentUser || await waitForFirebaseUser();

  if (user) {
    // No forced refresh here; SDK refreshes on its own
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- RESPONSE ----------
let isRefreshing = false;

axiosWithAuth.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;

    // Retry ONCE on 401 with a forced token refresh
    if (status === 401 && !original.__isRetry) {
      try {
        const user = getAuth().currentUser || await waitForFirebaseUser();
        if (user) {
          if (!isRefreshing) isRefreshing = true;
          const fresh = await user.getIdToken(true); // force refresh once
          isRefreshing = false;

          original.__isRetry = true;
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
          return axiosWithAuth(original);
        }
      } catch {
        // fall through to final 401 handler
      }
    }

    // Final 401 handling (no more retries)
    if (status === 401) {
      try { localStorage.clear(); } catch {}
      if (dispatchRef) {
        try { dispatchRef(clearAuthState()); } catch {}
      }
      // Avoid importing router here to keep it decoupled
      window.location.href = '/signin';
    }

    return Promise.reject(err);
  }
);

export default axiosWithAuth;
